/**
 * Shared utilities for ToolsTable and ToolsHierarchy (tool states: `ToolStateBadge`).
 *
 * @module features/toolView/utils/toolViewHelpers
 */

/**
 * Format a nullable ISO date string for display in the current locale.
 *
 * @param dateStr - ISO date string or null (null means no trace date available).
 * @param language - Current UI language code ('fr' | 'en' | 'de').
 * @param t - Translation function from useTranslation.
 * @param noTraceKey - i18n key to return when dateStr is null or invalid.
 *   Each caller passes its own key so that table and hierarchy can use different strings.
 */
export function formatDate(
  dateStr: string | null,
  language: string,
  t: (key: string) => string,
  noTraceKey: string
): string {
  if (dateStr === null) {
    return t(noTraceKey);
  }
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return t(noTraceKey);
    }
    const localeMap: Record<string, string> = {
      fr: 'fr-FR',
      en: 'en-US',
      de: 'de-DE',
    };
    const locale = localeMap[language] ?? 'fr-FR';
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return t(noTraceKey);
  }
}
